import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { createPortal } from 'react-dom'
import { ArrowLeft, Building2, Check, ChevronDown, Plus, Search, X } from 'lucide-react'
import {
  buildIdeas, bySpecificity, fitsBusiness, ideaSearchText, indexCatalog, madeFor, matcher,
  type BusinessChoice, type Idea,
} from '@/lib/discovery'
import type { DiscoveryCatalog, DiscoverySolution } from '@/types'

// ---------------------------------------------------------------------------
// "Find an idea" — every idea in the catalogue, findable two ways: one search
// across everything, or browse by WHAT YOU WANT TO BUILD (the solution types
// under their data-defined groups) or by PART OF YOUR BUSINESS (the areas).
// The user's business narrows it and puts the ideas made for it first. An
// idea opens a detail panel: what you get, the input that helps most, then
// fill in the blanks or a few questions. Ideas are examples, not limits — the
// page always offers "describe it in your own words".
// ---------------------------------------------------------------------------

export type IdeaUseMode = 'blanks' | 'guided' | 'describe'

interface Props {
  catalog: DiscoveryCatalog
  business: BusinessChoice | null
  /** Open on one group (`g:<id>`), kind (`k:<id>`) or area (`a:<id>`). */
  initialCategory?: string | null
  onChangeBusiness: () => void
  onClearBusiness: () => void
  onBack: () => void
  onUse: (idea: Idea, mode: IdeaUseMode) => void
  /** The hub's picker for a solution's "helps most" input, when it has one. */
  helpsMostAction: (solution: DiscoverySolution) => (() => void) | null
}

type Lens = 'kind' | 'area'

export default function IdeasBrowser({
  catalog, business, initialCategory, onChangeBusiness, onClearBusiness, onBack, onUse, helpsMostAction,
}: Props) {
  const idx = useMemo(() => indexCatalog(catalog), [catalog])
  const ideas = useMemo(() => buildIdeas(catalog), [catalog])
  const searchText = useMemo(() => new Map(ideas.map((i) => [i.key, ideaSearchText(i, idx)])), [ideas, idx])
  const bizId = business?.id || null

  const [q, setQ] = useState('')
  const [lens, setLens] = useState<Lens>(initialCategory?.startsWith('a:') ? 'area' : 'kind')
  const [cat, setCat] = useState<string>(initialCategory || (bizId ? 'best' : 'all'))
  const [sel, setSel] = useState<Idea | null>(null)
  const searchRef = useRef<HTMLInputElement>(null)

  useEffect(() => { searchRef.current?.focus({ preventScroll: true }) }, [])
  // A business picked (or cleared) here re-centres the list on it.
  useEffect(() => {
    setCat((c) => (c === 'best' || c === 'all' ? (bizId ? 'best' : 'all') : c))
  }, [bizId])

  const pool = ideas.filter((i) => fitsBusiness(i, bizId))
  const inCat = (i: Idea, c: string): boolean => {
    if (c === 'best') return madeFor(i, bizId)
    if (c === 'all') return true
    const [k, id] = [c.slice(0, 1), c.slice(2)]
    if (k === 'g') return idx.solutions[i.solution]?.group === id
    if (k === 'k') return i.solution === id
    if (k === 'a') return i.area === id
    return false
  }
  const count = (c: string) => pool.filter((i) => inCat(i, c)).length
  const current = q.trim() ? '' : cat

  const navItem = (c: string, label: string, opts: { strong?: boolean; indent?: boolean } = {}) => {
    const n = count(c)
    if (!n && c !== 'all') return null
    const on = current === c
    return (
      <button key={c} type="button" aria-current={on ? 'true' : undefined}
        onClick={() => { setCat(c); setQ('') }} style={navBtn(on, opts)}>
        <span style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>{label}</span>
        <span style={{ fontSize: '0.8425rem', color: on ? 'var(--color-accent)' : 'var(--color-text-faint)' }}>{n}</span>
      </button>
    )
  }

  const hit = matcher(q)
  // A search looks at EVERY idea — a word the user typed is worth more than
  // the business filter — with the ones that fit the business first.
  const list = q.trim()
    ? ideas.filter((i) => hit(searchText.get(i.key) || '')).sort((a, b) => (
      Number(fitsBusiness(b, bizId)) - Number(fitsBusiness(a, bizId)) || bySpecificity(bizId)(a, b)
    ))
    : pool.filter((i) => inCat(i, cat)).sort(bySpecificity(bizId))

  const catTitle = (): string => {
    if (q.trim()) return `Matches for “${q.trim()}”`
    if (cat === 'best') return `Made for ${business?.name.toLowerCase()}`
    if (cat === 'all') return business ? `Everything that fits ${business.name.toLowerCase()}` : 'Every idea'
    const id = cat.slice(2)
    if (cat.startsWith('g:')) return idx.groups[id]?.name || ''
    if (cat.startsWith('k:')) return idx.solutions[id]?.title || ''
    return idx.areas[id] || ''
  }

  const groups = catalog.solution_groups || []
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <button type="button" onClick={onBack} style={backLink}><ArrowLeft size={14} /> Back to start</button>
      <div>
        <h1 style={{ margin: 0, fontSize: '1.9625rem', lineHeight: 1.2, fontWeight: 600 }}>Find an idea</h1>
        <p style={{ margin: '6px 0 0', color: 'var(--color-text-secondary)' }}>
          Every idea is a starting point. You'll shape it before anything is built.
        </p>
      </div>

      <div style={bigSearch}>
        <Search size={19} style={{ color: 'var(--color-text-faint)', flex: 'none' }} />
        <input ref={searchRef} type="text" autoComplete="off" aria-label="Search ideas" value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search ideas — try leave, invoice, QR scan, dealer, booking"
          style={{ ...bareInput, fontSize: '1.0825rem' }} />
        {q && (
          <button type="button" className="btn btn-ghost btn-sm" aria-label="Clear search" onClick={() => setQ('')}>
            <X size={16} />
          </button>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <BusinessChip business={business} onClick={onChangeBusiness} prefix="For: " />
          {business && (
            <button type="button" className="btn btn-ghost btn-sm" aria-label="Show ideas for every business" onClick={onClearBusiness}>
              <X size={15} />
            </button>
          )}
        </div>
        <div role="group" aria-label="Browse by" style={segment}>
          {([['kind', 'What you want to build'], ['area', 'Part of your business']] as Array<[Lens, string]>).map(([l, label]) => (
            <button key={l} type="button" aria-pressed={lens === l} style={segBtn(lens === l)}
              onClick={() => { setLens(l); setCat(bizId ? 'best' : 'all'); setQ('') }}>
              {label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'flex-start' }}>
        <nav aria-label="Categories" style={{ flex: '1 1 220px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {bizId && navItem('best', `Made for ${business?.name.toLowerCase()}`)}
          {navItem('all', business ? 'Everything that fits' : 'Everything')}
          {lens === 'kind'
            ? groups.map((g) => (
              <div key={g.id} style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 10 }}>
                {navItem(`g:${g.id}`, g.name, { strong: true })}
                {catalog.solutions.filter((s) => s.group === g.id).map((s) => navItem(`k:${s.id}`, s.title, { indent: true }))}
              </div>
            ))
            : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 10 }}>
                {catalog.areas.map((a) => navItem(`a:${a.id}`, a.name))}
              </div>
            )}
        </nav>

        <section aria-live="polite" style={{ flex: '999 1 520px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <h2 style={{ margin: 0, fontSize: '1.1625rem', fontWeight: 600 }}>{catTitle()}</h2>
            <span style={{ fontSize: '0.9125rem', color: 'var(--color-text-secondary)' }}>
              {list.length === 1 ? '1 idea' : `${list.length} ideas`}
            </span>
          </div>
          {list.length > 0 ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 10 }}>
              {list.map((i) => (
                <button key={i.key} type="button" onClick={() => setSel(i)} style={card}>
                  <span style={{ fontWeight: 600, lineHeight: 1.35 }}>{i.title}</span>
                  <span style={{ flex: 1, fontSize: '0.9225rem', lineHeight: 1.45, color: 'var(--color-text-secondary)' }}>{i.description}</span>
                  <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <span style={tag(false)} title={idx.solutions[i.solution]?.title}>{idx.solutions[i.solution]?.title}</span>
                    {madeFor(i, bizId) && <span style={tag(true)}>Made for your business</span>}
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <div style={emptyBox}>
              <span style={{ fontSize: '1.0625rem' }}>Nothing here matches “{q.trim()}” yet — and that's fine.</span>
              <span style={{ fontSize: '0.9425rem', color: 'var(--color-text-secondary)' }}>Twasta builds what you describe, listed or not.</span>
              <button type="button" className="btn btn-primary" onClick={onBack}>Describe it in your own words</button>
            </div>
          )}
          {list.length > 0 && (
            <p style={{ margin: '6px 0 0', fontSize: '0.9425rem', color: 'var(--color-text-secondary)' }}>
              Don't see it?{' '}
              <button type="button" onClick={onBack} style={inlineLink}>Describe it in your own words</button>
              {' '}— the ideas are examples, not limits.
            </p>
          )}
        </section>
      </div>

      {sel && (
        <IdeaDetail
          idea={sel}
          businessName={business?.name || null}
          made={madeFor(sel, bizId)}
          solution={idx.solutions[sel.solution] || null}
          areaName={sel.area ? idx.areas[sel.area] || '' : ''}
          helpsMost={idx.solutions[sel.solution] ? helpsMostAction(idx.solutions[sel.solution]) : null}
          onClose={() => setSel(null)}
          onUse={(mode) => { const i = sel; setSel(null); onUse(i, mode) }}
        />
      )}
    </div>
  )
}

/** The "Your business ▾" chip — the same control on the start page and here. */
export function BusinessChip({ business, onClick, prefix = '', disabled }: {
  business: BusinessChoice | null
  onClick: () => void
  prefix?: string
  disabled?: boolean
}) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} aria-haspopup="dialog"
      title={business ? `Your business: ${business.name} — change` : 'Tell us your business for ideas that fit'}
      style={businessChip(!!business)}>
      <Building2 size={15} style={{ flex: 'none' }} />
      <span style={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {business ? `${prefix}${business.name}` : 'Your business'}
      </span>
      <ChevronDown size={14} style={{ flex: 'none' }} />
    </button>
  )
}

function IdeaDetail({ idea, businessName, made, solution, areaName, helpsMost, onClose, onUse }: {
  idea: Idea
  businessName: string | null
  made: boolean
  solution: DiscoverySolution | null
  areaName: string
  helpsMost: (() => void) | null
  onClose: () => void
  onUse: (mode: IdeaUseMode) => void
}) {
  const closeRef = useRef(onClose)
  closeRef.current = onClose
  const firstRef = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    firstRef.current?.focus({ preventScroll: true })
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeRef.current() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  return createPortal(
    <div style={drawerBackdrop} onClick={onClose}>
      <aside role="dialog" aria-modal="true" aria-labelledby="idea-title" style={drawer} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
          <span style={{ fontSize: '0.8825rem', color: 'var(--color-text-secondary)' }}>
            {solution?.title}{areaName ? ` · ${areaName}` : ''}
          </span>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose} aria-label="Close"><X size={18} /></button>
        </div>
        <div>
          <h2 id="idea-title" style={{ margin: 0, fontSize: '1.4625rem', lineHeight: 1.25, fontWeight: 600 }}>{idea.title}</h2>
          <p style={{ margin: '8px 0 0', fontSize: '1.0425rem', lineHeight: 1.5, color: 'var(--color-text-secondary)' }}>{idea.description}.</p>
        </div>
        {idea.whatYouGet.length > 0 && (
          <div>
            <h3 style={{ margin: '0 0 8px', fontSize: '0.9625rem', fontWeight: 600 }}>What you get</h3>
            <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {idea.whatYouGet.map((w) => (
                <li key={w} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <Check size={16} style={{ color: 'var(--color-success)', flex: 'none', marginTop: 3 }} />{w}
                </li>
              ))}
            </ul>
          </div>
        )}
        <div style={fitBox}>
          {made
            ? `Made with ${businessName?.toLowerCase()} in mind — the questions and defaults will fit your business.`
            : 'Works for most businesses. A few questions will tailor it to yours.'}
        </div>
        {solution?.helps_most && (
          <div style={helpsBox}>
            <span style={{ flex: 1, minWidth: 0, color: 'var(--color-text-secondary)' }}>
              <b style={{ color: 'var(--color-text)', fontWeight: 500 }}>Helps most:</b> {solution.helps_most}
            </span>
            {helpsMost && (
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => { onUse('describe'); helpsMost() }}>
                <Plus size={14} /> Add it
              </button>
            )}
          </div>
        )}
        <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button ref={firstRef} type="button" className="btn btn-primary" style={{ height: 46, justifyContent: 'center' }} onClick={() => onUse('blanks')}>
            Use this idea
          </button>
          <button type="button" className="btn btn-secondary" style={{ height: 46, justifyContent: 'center' }} onClick={() => onUse('guided')}>
            Shape it with a few questions
          </button>
          <button type="button" onClick={() => onUse('describe')} style={{ ...inlineLink, alignSelf: 'center', padding: 8 }}>
            Put it in the box and edit it myself
          </button>
        </div>
      </aside>
    </div>,
    document.body,
  )
}

// ── styles ────────────────────────────────────────────────────────────
const backLink: CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, alignSelf: 'flex-start', background: 'none', border: 0,
  padding: '6px 0', fontSize: '0.9425rem', color: 'var(--color-text-secondary)', cursor: 'pointer',
}
const inlineLink: CSSProperties = {
  background: 'none', border: 0, padding: 0, font: 'inherit', color: 'var(--color-accent)',
  cursor: 'pointer', textDecoration: 'underline',
}
/** An input inside a styled box: drop the global field chrome (border, focus
 *  ring, padding), or it draws a second box inside the first. */
const bareInput: CSSProperties = {
  flex: 1, minWidth: 0, height: '100%', border: 0, borderRadius: 0, padding: 0, boxShadow: 'none',
  outline: 'none', background: 'transparent', font: 'inherit', color: 'var(--color-text)',
}
const bigSearch: CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 12, height: 54, padding: '0 16px',
  border: '1px solid var(--color-border-strong)', borderRadius: 14, background: 'var(--color-input-bg, var(--color-bg))',
}
const segment: CSSProperties = {
  display: 'inline-flex', gap: 4, padding: 4, borderRadius: 12, background: 'var(--color-bg-secondary)',
  border: '1px solid var(--color-border)', flexWrap: 'wrap',
}
const segBtn = (on: boolean): CSSProperties => ({
  font: 'inherit', fontSize: '0.9225rem', height: 34, padding: '0 14px', border: 0, borderRadius: 9, cursor: 'pointer',
  background: on ? 'var(--color-bg)' : 'transparent', color: on ? 'var(--color-text)' : 'var(--color-text-secondary)',
  fontWeight: on ? 500 : 400, boxShadow: on ? '0 1px 2px rgba(0,0,0,.12)' : 'none',
})
const navBtn = (on: boolean, { strong, indent }: { strong?: boolean; indent?: boolean }): CSSProperties => ({
  font: 'inherit', fontSize: '0.9425rem', display: 'flex', alignItems: 'center', gap: 8, width: '100%',
  minHeight: 36, padding: `6px 10px 6px ${indent ? 22 : 10}px`, border: 0, borderRadius: 8, cursor: 'pointer',
  background: on ? 'var(--color-accent-weak)' : 'transparent',
  color: on ? 'var(--color-accent)' : 'var(--color-text)',
  fontWeight: on || strong ? 600 : 400,
})
const card: CSSProperties = {
  font: 'inherit', display: 'flex', flexDirection: 'column', gap: 8, minHeight: 140, padding: 14,
  border: '1px solid var(--color-border)', borderRadius: 12, background: 'var(--color-bg)',
  color: 'var(--color-text)', textAlign: 'left', cursor: 'pointer',
}
const tag = (good: boolean): CSSProperties => ({
  fontSize: '0.8125rem', padding: '2px 8px', borderRadius: 999, maxWidth: '100%',
  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  background: good ? 'var(--color-success-weak)' : 'var(--color-bg-secondary)',
  color: good ? 'var(--color-success)' : 'var(--color-text-secondary)',
  border: good ? 'none' : '1px solid var(--color-border)',
})
const emptyBox: CSSProperties = {
  padding: 28, border: '1px dashed var(--color-border-strong)', borderRadius: 14, textAlign: 'center',
  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
}
const businessChip = (set: boolean): CSSProperties => ({
  font: 'inherit', fontSize: '0.9225rem', display: 'inline-flex', alignItems: 'center', gap: 7, height: 38,
  padding: '0 11px', borderRadius: 10, cursor: 'pointer',
  border: set ? '1px solid var(--color-accent)' : '1px dashed var(--color-border-strong)',
  background: set ? 'var(--color-accent-weak)' : 'transparent',
  color: set ? 'var(--color-accent)' : 'var(--color-text-secondary)',
  fontWeight: set ? 500 : 400,
})
const drawerBackdrop: CSSProperties = {
  position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(14,17,23,.38)', display: 'flex', justifyContent: 'flex-end',
}
const drawer: CSSProperties = {
  width: '100%', maxWidth: 440, height: '100%', boxSizing: 'border-box', overflowY: 'auto', padding: 22,
  background: 'var(--color-bg)', color: 'var(--color-text)', borderLeft: '1px solid var(--color-border)',
  boxShadow: '-24px 0 48px -24px rgba(0,0,0,.4)', display: 'flex', flexDirection: 'column', gap: 18,
}
const fitBox: CSSProperties = {
  padding: '10px 12px', borderRadius: 10, background: 'var(--color-bg-secondary)', fontSize: '0.9425rem',
  color: 'var(--color-text-secondary)',
}
const helpsBox: CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 10, border: '1px dashed var(--color-border-strong)',
  borderRadius: 10, padding: '10px 12px', fontSize: '0.9225rem',
}
